import React, { memo, useMemo } from "react";
import { useQuery } from "react-query";
import { useRouteMatch } from "react-router-dom";

import Preloader from "~/components/Preloader";
import { EmployeeService } from "~/entity/Employee/api/service";
import { mergeEmployeeCardWithGaps } from "~/entity/Employee/lib/restGaps";
import { useGetEmployeeCardSupplementQuery } from "~/schemas/generated/graphql";

import EmployeeProfileForm from "./EmployeeProfileForm";

const EmployeeProfileEditor = () => {
  const { params } = useRouteMatch<{ id: string }>();
  const personalId = params.id;
  const isAdd = personalId === "add";

  const {
    data: cardResponse,
    isLoading: cardLoading,
    refetch: refetchCard,
  } = useQuery(
    ["employeeCard", personalId],
    () =>
      EmployeeService.GET.card(personalId).then((response) => response.data),
    {
      enabled: !isAdd,
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const {
    data: supplement,
    loading: supplementLoading,
    refetch: refetchSupplement,
  } = useGetEmployeeCardSupplementQuery({
    variables: { id: personalId },
    skip: isAdd,
    fetchPolicy: "no-cache",
  });

  const employee = useMemo(() => {
    const card = cardResponse?.employee;
    if (!card) {
      return null;
    }
    return mergeEmployeeCardWithGaps(card, supplement);
  }, [cardResponse, supplement]);

  if (isAdd) {
    return <EmployeeProfileForm />;
  }

  if (cardLoading || supplementLoading || !employee) {
    return <Preloader />;
  }

  return (
    <EmployeeProfileForm
      employee={employee}
      refetch={() => {
        void refetchCard();
        void refetchSupplement();
      }}
    />
  );
};

export default memo(EmployeeProfileEditor);
